import { Button, Icon, Link, Notification } from '@arcadiapower/shrike';
import { formatPhoneNumber } from '@arcadiapower/warbler';
import { useContext, useEffect } from 'react';
import { EditInformationDialog, ResetWarningDialog } from '~/components/dialogs';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Text, Description, Title } from '~/components/typography';
import { useCopy } from '~/hooks/use-copy';
import { TelesalesEditUserInfo, TelesalesFunnelContext } from '~/machines/telesales-funnel';
import { deleteCookies } from '~/utils/for-warbler';
import { CustomerDetailsContainer } from './telesales-complete.style';

const EmailResultNotifier = ({
  emailResult,
}: {
  emailResult?: { totalSent: number; totalFailed: number; errorMessage?: string };
}) => {
  const { errorMessage } = emailResult || {};
  const { addNotification } = useContext(Notification.Context);

  const addSucessNotification = () => {
    addNotification({
      displayTime: 5000,
      message: 'Email has been sent to the customer!',
      variant: 'success',
    });
  };

  const addErrorNotification = () => {
    addNotification({
      displayTime: 5000,
      message: errorMessage || 'Please check the user details and try again.',
      variant: 'error',
    });
  };

  useEffect(() => {
    if (emailResult?.totalSent && emailResult.totalSent > 0) {
      addSucessNotification();
    } else if (emailResult?.totalFailed && emailResult.totalFailed > 0) {
      addErrorNotification();
    }
  }, [emailResult]);

  return null;
};

export const TelesalesComplete = ({
  context,
  onSubmitEditInformation,
  overrideCopy,
  resendEmailHandler,
  emailResult,
}: TelesalesCompleteProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const handleMarkCompleted = () => {
    localStorage.clear();
    deleteCookies();
    location.reload();
  };

  const { firstName, lastName, email, phone } = context.funnelPacket!.user;

  const fullName = `${firstName} ${lastName}`;
  const phoneNumber = formatPhoneNumber(phone || '');

  return (
    <TwoColumnGrid testId="screen-TelesalesComplete">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description>{copy.description}</Description>
        <CustomerDetailsContainer
          gridColumn="auto / span 2"
          display="flex"
          justifyContent="space-between"
          p={24}
          mb={28}
        >
          <Container>
            <Text mb={4} textStyle="heading700">
              {fullName}
            </Text>
            <Text>
              <Icon icon="Phone" margin={{ right: '12px' }} />
              {phoneNumber}
            </Text>
            <Text mb={4}>
              <Icon icon="Envelope" margin={{ right: '12px' }} />
              {email}
            </Text>
            <EditInformationDialog
              user={context.funnelPacket!.user}
              target={<Link color="primary">Edit information</Link>}
              handleSubmit={onSubmitEditInformation}
            />
          </Container>
        </CustomerDetailsContainer>
        <Description mb={5}>{copy.description2}</Description>
        <Description mb={5}>{copy.description3}</Description>
        <ResetWarningDialog
          target={
            <Button mode="primary" data-testid="mark-complete-button">
              Mark completed
            </Button>
          }
          handleReset={handleMarkCompleted}
        />
        <EmailResultNotifier emailResult={emailResult} />
        <Button mode="default" onClick={resendEmailHandler} data-testid="resend-email-button">
          <Icon icon="Link" margin={{ right: '8px' }} />
          {copy.telesalesReshareButton}
        </Button>
      </TwoColumnGrid.Primary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = (context: TelesalesFunnelContext) => {
  const needsTerms = context.billingSelection === 'ucb' || !!context.paymentMethod;
  const nextStep = needsTerms
    ? 'sign terms of agreement and finish'
    : 'add their payment method and continue';

  return {
    title: "Let's make sure everything looks good",
    description: `Email has been sent to customer with magic link and instructions to ${nextStep} the sign up process.`,
    description2:
      'Please, ask customer to check their inbox. Click "Mark completed" if user has received and completed the process.',
    description3:
      'If the customer didn’t receive the email you can resend the link using the "Resend email to customer" button below.',
    telesalesReshareButton: 'Resend email to customer',
  };
};

export type TelesalesCompleteCopy = ReturnType<typeof defaultCopy>;

export interface TelesalesCompleteProps {
  context: TelesalesFunnelContext;
  onSubmitEditInformation: (data: TelesalesEditUserInfo) => void;
  overrideCopy?: (context: TelesalesFunnelContext) => Partial<TelesalesCompleteCopy>;
  resendEmailHandler: () => void;
  emailResult?: { totalSent: number; totalFailed: number };
}
